import type { Pead } from "epsm-api-types/models";
import {
	formatValueDate,
	formatValuePercent,
} from "@/components/widgets/helpers/FormattingHelpers";
import config from "../../../config";
import { NO_DATA, PEAD } from "../../../consts";
import type { DriftType } from "../../../types";
import { getDriftTypeDescription } from "../getDriftTypeDescription/getDriftTypeDescription";
import { getYAxisRange } from "../getYAxisRange";

const { CHART_LINES, COLORS, MIN_MAX_PADDING_IN_PERCENT } = config;

export function getChartOptions(
	relativeToSpy: boolean,
	timeLine: number[],
	series: echarts.SeriesOption[],
	pead: Pead | null,
	isMainChart: boolean,
	currentPeadAccy?: number | string,
	stdDev?: boolean,
): echarts.EChartsOption {
	const legendData = isMainChart ? CHART_LINES.slice(0, 4) : [];
	const ticker = pead?.ticker;
	const dateNow = formatValueDate(pead?.earning_day);
	const tickerAndDate = isMainChart ? `${ticker} ${dateNow}` : "";
	const driftUnit = pead?.drift_unit?.name;
	const driftDescription =
		driftUnit === "seasonal"
			? `Quarter ${pead?.drift_unit.value}`
			: `${getDriftTypeDescription(pead?.drift_unit?.value as DriftType)} (Drift ${pead?.drift_unit?.value})`;

	const accuracy = pead?.accuracy?.find((el) => {
		if (driftUnit === "seasonal") {
			return !el.depends_on_type && el.drift_point === "qtr_end";
		}

		return el.depends_on_type && el.drift_point === "qtr_end";
	})?.value;

	const sample = `${isMainChart ? "Sample" : "Spl"} ${pead?.sample_count}/${pead?.overall_count}`;
	const watermarkFont = "bold 12px sans-serif";

	const yAxisRange = getYAxisRange(
		relativeToSpy,
		pead,
		series,
		stdDev,
		isMainChart,
	);
	const { min, max } = yAxisRange;

	const averageMove = `Avg move: ${formatValuePercent(pead?.upside, "N/A")}`;

	return {
		legend: {
			data: legendData,
			bottom: 12,
		},
		backgroundColor: COLORS.BACKGROUND,
		tooltip: {
			trigger: "item",
			backgroundColor: "var(--gray)",
			textStyle: {
				color: "var(--white)",
			},
		},
		grid: {
			show: true,
			top: 5,
			left: 0,
			right: 15,
			bottom: 0,
			containLabel: true,
			borderWidth: 1,
			borderColor: COLORS.BORDER,
		},
		xAxis: {
			type: "category",
			splitLine: {
				show: false,
			},
			axisLabel: {
				margin: 5,
				fontSize: 10,
			},
			data: timeLine,
		},
		yAxis: {
			type: "value",
			axisLabel: {
				margin: 5,
				fontSize: 10,
				formatter: (value) => Math.trunc(value).toString(),
			},
			splitLine: {
				show: false,
			},
			min,
			max: isMainChart ? max : max * (1 + MIN_MAX_PADDING_IN_PERCENT),
		},
		graphic: [
			{
				type: "text",
				left: "25",
				top: "10",
				z: 4,
				style: {
					fill: PEAD.WATERMARK_COLOR,
					text: tickerAndDate,
					font: watermarkFont,
				},
			},
			{
				type: "text",
				left: "25",
				top: isMainChart ? "25" : "10",
				z: 4,
				style: {
					fill: driftDescription?.startsWith("Negative")
						? PEAD.LINE_COLOR.MIN
						: PEAD.LINE_COLOR.MAX,
					text: driftDescription,
					font: watermarkFont,
				},
			},
			{
				type: "text",
				left: "25",
				top: isMainChart ? "40" : "22",
				z: 4,
				style: {
					fill: PEAD.WATERMARK_COLOR,
					text: accuracy
						? `Accy: ${formatValuePercent(accuracy)}`
						: `Accy: ${currentPeadAccy === NO_DATA ? currentPeadAccy : `${currentPeadAccy}%`}`,
					font: watermarkFont,
				},
			},
			{
				type: "text",
				left: "105",
				top: isMainChart ? "40" : "22",
				z: 4,
				style: {
					fill: PEAD.WATERMARK_COLOR,
					text: sample,
					font: watermarkFont,
				},
			},
			{
				type: "text",
				left: "25",
				top: isMainChart ? "55" : "35",
				z: 4,
				style: {
					fill: PEAD.WATERMARK_COLOR,
					text: averageMove,
					font: watermarkFont,
				},
			},
		],

		series:
			isMainChart && series ? series : series.filter((s) => s.name !== "Price"),
	};
}
